import { useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { useAppSelector } from "../../../redux/hooks";
import { Checkbox } from "components/fields/base/Checkbox";
import { Flex } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css/css.mjs";
import { mapCountryCodeToName } from "views/amc/utils";

const AmazonAccountsTable = ({
  accountData,
  columnsToRender = [],
  isLoading,
  additionalColumns = [],
  defaultSorted,
  isSelectable = false,
  selectionKey = "id",
  defaultPageSize = 10,
  allowEntityIdSelection = false,
  selectedEntityIds = [],
  savedEntityIds = [],
  savedAMCProfiles = [],
  onEntityIdChange = () => {},
  setSelected = () => {},
  selected,
  hideBottomPagination = false,
}) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef();

  const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

  const columnOptions = [
    buildTextColumn("name", "Organization Name"),
    buildTextColumn("countryCodes", "Marketplaces", {
      width: 130,
      formatter: (v) => {
        return v.join(", ");
      },
    }),
    buildTextColumn("adsAccountId", "Ad Account ID", { width: 350 }),
    buildTextColumn("instanceName", "Instance Name", {
      metric: {
        id: "",
        format: "text",
        accessor: (d) => d,
        name: "Instance Name",
      },
      formatter: (row) => {
        const instanceName = row.alternateIds?.find((entity) => "instance_name" in entity)?.instance_name ?? "";
        return <span>{instanceName}</span>;
      },
    }),
    buildTextColumn("instanceId", "Instance ID", {
      metric: {
        id: "",
        format: "text",
        accessor: (d) => d,
        name: "Instance ID",
      },
      formatter: (row) => {
        const instanceId = row.alternateIds?.find((entity) => "instance_id" in entity)?.instance_id ?? "";
        return <span>{instanceId}</span>;
      },
    }),
    buildTextColumn("entityIds", "Entity IDs", {
      metric: {
        id: "",
        format: "text",
        accessor: (d) => d,
        name: "Enitity IDs By Marketplace",
      },
      className: "text-start justify-content-start px-5 h-100",
      cellContainerClasses: "text-start justify-content-start",
      width: 400,
      formatter: (row) => {
        const entityIdsByMarketplace = row.alternateIds?.filter((id) => "entityId" in id);

        const adsAccountId = row.adsAccountId;

        if (allowEntityIdSelection) {
          return (
            <Flex direction="column" spacing="small">
              <h3 className={css({ fontWeight: "bold", fontSize: "medium" })}>Entity IDs</h3>
              {entityIdsByMarketplace
                ?.sort((a, b) => {
                  const countryA = mapCountryCodeToName(a.countryCode);
                  const countryB = mapCountryCodeToName(b.countryCode);
                  return (
                    countryA < countryB ? -1
                    : countryA > countryB ? 1
                    : 0
                  );
                })
                ?.map(({ countryCode, entityId }) => {
                  return (
                    <Flex key={entityId} gap="small" className={css({ padding: "5px 0" })}>
                      <Checkbox
                        size="small"
                        value={
                          selectedEntityIds?.includes(entityId) ||
                          savedEntityIds?.map((entity) => entity.entityId)?.includes(entityId)
                        }
                        onChange={(checked) => {
                          onEntityIdChange({ entityId, countryCode }, checked);
                        }}
                        isDisabled={
                          !selected?.includes(adsAccountId) ||
                          savedEntityIds?.map((entity) => entity.entityId)?.includes(entityId)
                        }
                      />
                      <span>
                        {mapCountryCodeToName(countryCode)}: {entityId}
                      </span>
                    </Flex>
                  );
                })}
            </Flex>
          );
        }
        return (
          <ul className="text-start">
            {entityIdsByMarketplace
              ?.sort((a, b) => {
                const countryA = mapCountryCodeToName(a.countryCode);
                const countryB = mapCountryCodeToName(b.countryCode);
                return (
                  countryA < countryB ? -1
                  : countryA > countryB ? 1
                  : 0
                );
              })
              ?.map(({ countryCode, entityId }) => (
                <li key={entityId}>
                  {countryNames.of(countryCode)}: {entityId}
                </li>
              ))}
          </ul>
        );
      },
    }),
    buildTextColumn("profileIds", "Profile IDs", {
      metric: {
        id: "",
        format: "text",
        accessor: (d) => d,
        name: "Profile IDs By Marketplace",
      },
      className: "text-start justify-content-start px-5 h-100",
      cellContainerClasses: "text-start justify-content-start",

      formatter: (row) => {
        const profileIdsByMarketplace = row.alternateIds?.filter((id) => "profileId" in id);

        return (
          <ul className="text-start">
            {profileIdsByMarketplace
              ?.sort((a, b) => {
                const countryA = mapCountryCodeToName(a.countryCode);
                const countryB = mapCountryCodeToName(b.countryCode);
                return (
                  countryA < countryB ? -1
                  : countryA > countryB ? 1
                  : 0
                );
              })
              ?.map(({ countryCode, profileId }) => (
                <li key={profileId}>
                  {countryNames.of(countryCode)}: {profileId}
                </li>
              ))}
          </ul>
        );
      },
    }),
    ...additionalColumns,
  ];

  const tableData = useMemo(() => {
    return accountData;
  }, [accountData]);

  const columnSpecs = columnsToRender.map((renderColumnKey) =>
    columnOptions.find((option) => option.key === renderColumnKey),
  );

  return (
    <Row className={`mx-0 w-100 ${hideBottomPagination ? "pb-5" : ""}`}>
      <Col xs={12} className="mx-auto px-0">
        <BulkActionsTable
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={"trellisAmazonProfilesTable"}
          titleCol={{
            id: "name",
            isStatic: true,
            Header: "Name",
            accessor: "account_name",
            style: { width: "450px" },
            width: 150,
            Cell: ({ row }) => {
              const { accountName: name } = row._original;

              return <div className="d-flex align-items-center overflow-ellipsis">{name}</div>;
            },
            filterMethod: () => {
              return;
            },
            Filter: () => null,
          }}
          columnSpecs={columnSpecs}
          data={tableData}
          hideFilterToggleIcon={true}
          isSelectable={isSelectable}
          selected={selected}
          setSelected={setSelected}
          isExpandable={false}
          selectionKey={selectionKey}
          disableSelectionList={savedAMCProfiles}
          hideSelectAll={true}
          defaultSorted={defaultSorted || [{ id: "name", desc: false }]}
          defaultPageSize={defaultPageSize}
          getSearchCriteria={(row) => {
            const { accountName, adsAccountId, alternateIds } = row;

            const joinedAlternateIds = alternateIds
              ?.map((id) =>
                id.entityId ? id.entityId
                : id.profileId ? id.profileId.toString()
                : null,
              )
              ?.filter((id) => id)
              ?.join("");
            return `${accountName} ${adsAccountId} ${joinedAlternateIds}`;
          }}
          tableRowDescription={"Amazon Account"}
          minRows={0}
          isLoading={isLoading}
          hideBottomPagination={hideBottomPagination}
        />
      </Col>
    </Row>
  );
};

export default AmazonAccountsTable;
